import type { ColumnsType } from 'antd/es/table'
import { Typography, Tooltip } from 'antd'
import { StarFilled } from '@ant-design/icons'
import Contractor from '../../../types/api/contractor'
import ContractorContact from '../../../types/api/contractor-contact'
import ClientActionsEdit from '../components/cells/actions/actions-edit'
import ApprovalStatusCell from '../components/cells/approval-status/approval-status'

const { Text } = Typography

const subCellStyle: React.CSSProperties = {
    padding: '4px 8px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
}

const firstSubCellStyle: React.CSSProperties = {
    ...subCellStyle,
}

const getSortedContacts = (contacts: ContractorContact[] | undefined): ContractorContact[] => {
    if (!contacts || contacts.length === 0) return []
    
    return [...contacts].sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) return -1
        if (!a.isPrimary && b.isPrimary) return 1
        return 0
    })
}

const renderContactCells = (
    contacts: ContractorContact[] | undefined,
    getValue: (contact: ContractorContact) => string | undefined
) => {
    const sortedContacts = getSortedContacts(contacts)
    
    if (sortedContacts.length === 0) {
        return <Text type='secondary'>-</Text>
    }

    return (
        <div style={{ width: '100%' }}>
            {sortedContacts.map((contact, index) => {
                const value = getValue(contact)
                const style = index === 0 ? firstSubCellStyle : subCellStyle
                
                return (
                    <div key={contact.documentId || index} style={style}>
                        <Text>{value || '-'}</Text>
                    </div>
                )
            })}
        </div>
    )
}

const renderPrimaryContactIcons = (contacts: ContractorContact[] | undefined) => {
    const sortedContacts = getSortedContacts(contacts)
    
    if (sortedContacts.length === 0) {
        return null
    }

    return (
        <div style={{ width: '100%' }}>
            {sortedContacts.map((contact, index) => {
                const style = index === 0 ? firstSubCellStyle : subCellStyle
                const isPrimary = contact.isPrimary
                
                return (
                    <div key={contact.documentId || index} style={style}>
                        {isPrimary ? (
                            <Tooltip title='Основной контакт'>
                                <StarFilled style={{ color: '#1890ff', fontSize: '16px' }} />
                            </Tooltip>
                        ) : null}
                    </div>
                )
            })}
        </div>
    )
}

export const clientsColumns: ColumnsType<Contractor> = [
    {
        title: 'Название',
        dataIndex: 'name',
        key: 'name',
    },
    {
        title: 'Юр. адрес',
        dataIndex: 'legalAddress',
        key: 'legalAddress',
        render: (value: string | undefined) => value || '-',
    },
    {
        title: 'Комментарий',
        dataIndex: 'comment',
        key: 'comment',
        render: (value: string | undefined) => value || '-',
    },
    {
        title: 'Кураторы',
        key: 'curators',
        render: (_, record) => {
            const curators = record.curators || []
            if (curators.length === 0) return '-'

            return curators
                .map(curator => {
                    const username = curator.user?.username || '-'
                    const percent =
                        curator.percent !== undefined && curator.percent !== null
                            ? ` (${curator.percent}%)`
                            : ''
                    return `${username}${percent}`
                })
                .join(', ')
        },
    },
    {
        title: '',
        key: 'primaryContact',
        width: 50,
        align: 'center',
        render: (_, record) => renderPrimaryContactIcons(record.contractor_contacts),
    },
    {
        title: 'Адрес получателя',
        key: 'contactAddress',
        render: (_, record) =>
            renderContactCells(record.contractor_contacts, contact => contact.address),
    },
    {
        title: 'Наименование точки',
        key: 'contactPointName',
        render: (_, record) =>
            renderContactCells(record.contractor_contacts, contact => contact.pointName),
    },
    {
        title: 'Имя получателя',
        key: 'contactReceiverName',
        render: (_, record) =>
            renderContactCells(record.contractor_contacts, contact => contact.receiverName),
    },
    {
        title: 'Телефон',
        key: 'contactPhone',
        width: 180,
        render: (_, record) =>
            renderContactCells(record.contractor_contacts, contact => contact.phone),
    },
    {
        title: 'Часы работы',
        key: 'contactWorkingHours',
        render: (_, record) =>
            renderContactCells(record.contractor_contacts, contact => contact.workingHours),
    },
    {
        title: 'Комментарий к контакту',
        key: 'contactComment',
        render: (_, record) =>
            renderContactCells(record.contractor_contacts, contact => contact.comment),
    },
    {
        title: 'Статус',
        key: 'approvalStatus',
        width: 110,
        fixed: 'right',
        align: 'center',
        render: (_, record) => <ApprovalStatusCell contractor={record} />,
    },
    {
        title: 'Действия',
        key: 'actions',
        width: 100,
        fixed: 'right',
        align: 'center',
        render: (_, record) => <ClientActionsEdit id={record.documentId} />,
    },
]
